import { router } from "@inertiajs/react";
import { useCallback, useEffect, useState } from "react";

type ExportStatus = "idle" | "processing" | "ready" | "failed";

interface UseCompleteExportOptions {
  requestHref: string;
  statusHref: string;
}

interface FlashPayload {
  export_token?: string;
  notice?: string;
  alert?: string;
}

interface ExportStatusResponse {
  status: "processing" | "ready" | "failed";
  download_url?: string | null;
  error?: string | null;
}

const isExportStatusResponse = (
  payload: unknown,
): payload is ExportStatusResponse => {
  if (payload == null || typeof payload !== "object") return false;
  const data = payload as Record<string, unknown>;
  return (
    data.status === "processing" ||
    data.status === "ready" ||
    data.status === "failed"
  );
};

// Handles complete export request, polling, and auto-download once ready.
export function useCompleteExport({
  requestHref,
  statusHref,
}: UseCompleteExportOptions) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [status, setStatus] = useState<ExportStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const requestExport = useCallback(() => {
    if (isRequesting || status === "processing") return;

    setIsRequesting(true);
    setStatus("processing");
    setError(null);

    router.post(
      requestHref,
      {},
      {
        preserveScroll: true,
        onSuccess: (page) => {
          const flash = (page.props as { flash?: FlashPayload }).flash;
          const nextToken = flash?.export_token;
          if (!nextToken) {
            setStatus("idle");
            setError(
              flash?.notice ??
                flash?.alert ??
                "No se pudo iniciar la exportación",
            );
            return;
          }
          setToken(nextToken);
        },
        onError: () => {
          setStatus("failed");
          setError("Error al iniciar la exportación");
        },
        onFinish: () => setIsRequesting(false),
      },
    );
  }, [isRequesting, requestHref, status]);

  useEffect(() => {
    if (!token || status === "ready" || status === "failed") return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${statusHref}?token=${token}`);
        if (!response.ok) {
          setStatus("failed");
          setError("Error al verificar el estado de la exportación");
          return;
        }
        const data: unknown = await response.json();
        if (!isExportStatusResponse(data)) {
          setStatus("failed");
          setError("Error al verificar el estado de la exportación");
          return;
        }

        if (data.status === "ready") {
          setStatus("ready");
          setDownloadUrl(data.download_url);
        } else if (data.status === "failed") {
          setStatus("failed");
          setError(data.error ?? "Error al generar la exportación");
        }
      } catch {
        // Network error, continue polling.
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [status, statusHref, token]);

  useEffect(() => {
    if (status !== "ready" || !downloadUrl) return;

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setToken(null);
      setStatus("idle");
      setDownloadUrl(null);
      setError(null);
    }, 1000);
  }, [downloadUrl, status]);

  return {
    requestExport,
    isRequesting,
    status,
    error,
  };
}
